import {
  Component,
  OnInit,
  Input,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { IUProperty } from "src/app/shared/interface/umbraco-property";
import { IPictureData } from "../../shared/components/picture/picture.component";
import { IButtonData } from "src/app/shared/components/bg-button/bg-button.component";
import { ThumbnailBuilderService } from "../testimonials-panel/service/thumbnail-builder.service";
import get from "lodash.get";
import { MqService } from "../../service/mq.service";
import { IVideoPickerVideoData } from "src/app/shared/interface/video-picker-data";
import { applyTitleSettings } from "../../shared/utils/title-style-settings-utils";
import { ITitleStyleSettings } from "../../shared/interface/title-style-settings";
import { ILinkWithColor } from "src/app/shared/interface/link";
import Splide from "@splidejs/splide";

interface ITestimonialsPanelData {
  title?: IUProperty<string>;
  capitalizeTitle?: IUProperty<boolean>;
  items: IUProperty<ITestimonialsItemData[]>;
  link?: IUProperty<IButtonData>;
  linkColor?: IUProperty<string>;
  waves?: IUProperty<string[]>;
  bigPictures?: IUProperty<boolean>;
  itemAuthorSize?: IUProperty<string>;
  itemAuthorFontWeight?: IUProperty<string>;
  itemQuoteSize?: IUProperty<string>;
  itemQuoteFontWeight?: IUProperty<string>;
  itemSubtitleSize?: IUProperty<string>;
  itemSubtitleFontWeight?: IUProperty<string>;
  titleStyleSettings?: ITitleStyleSettings;
}

export interface ITestimonialsItemData {
  author?: IUProperty<string>;
  textColor: IUProperty<string>;
  position?: IUProperty<string>;
  subtitle?: IUProperty<string>;
  quote?: IUProperty<string>;
  readMoreDescriptionButton?: IUProperty<IBottonOptions[]>;
  media?: any;
  mobileMedia?: IUProperty<IPictureData>;
  link?: IUProperty<ILinkWithColor[]>;
  videoData: IVideoViewModel;
  imageData: IImageViewModel;
  state: string;
  isShow: boolean;
  bigPictures: boolean;
}

export interface IVideoViewModel {
  video: object;
  thumbnail: IPictureData;
  params: string;
}
interface IImageViewModel {
  desktopSrc: string;
  desktopAlt: string;
  mobileSrc: string;
  mobileAlt: string;
}

interface IBottonOptions {
  buttonText: IUProperty<string>;
  buttonOffText: IUProperty<string>;
  buttonTextSize: IUProperty<string>;
  buttonFontWeight: IUProperty<string>;
  buttonColor: IUProperty<string>;
}

@Component({
  selector: "app-testimonials-panel",
  templateUrl: "./testimonials-panel.component.html",
  styleUrls: ["./testimonials-panel.component.less"],
})
export class TestimonialsPanelComponent implements OnInit {
  @Input() data: ITestimonialsPanelData;

  @ViewChild("titleEl", { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild("wrapper", { static: false }) wrapperEl: ElementRef<HTMLElement>;
  @ViewChild("splide", { static: false }) splideRoot!: ElementRef;

  readonly tabletMediaQuery = "(min-width: 1024px)";
  items: ITestimonialsItemData[] = [];
  isMobile: boolean;
  topWave: boolean = false;
  bottomWave: boolean = false;
  forceHideQuoteId = 0;
  isReadMoreActive = false;

  private splideInstance: any;

  get isBigPictures() {
    return this.data.bigPictures && this.data.bigPictures.value;
  }

  constructor(
    private mq: MqService,
    private thumbnailBuilder: ThumbnailBuilderService
  ) {
    this.isMobile = this.mq.isMidi();
  }

  ngOnInit() {
    this.mapData();
  }

  ngAfterViewInit() {
    if (this.data.capitalizeTitle.value)
      this.titleEl.nativeElement.style.textTransform = "uppercase";

    applyTitleSettings(this.data, this.titleEl);

    if (this.data.itemAuthorSize && this.data.itemAuthorSize.value) {
      this.wrapperEl.nativeElement.style.setProperty(
        "--testimonials-panel-author-size",
        this.data.itemAuthorSize.value
      );
    }
    if (
      this.data.itemAuthorFontWeight &&
      this.data.itemAuthorFontWeight.value
    ) {
      this.wrapperEl.nativeElement.style.setProperty(
        "--testimonials-panel-author-font-weight",
        this.data.itemAuthorFontWeight.value
      );
    }
    if (this.data.itemQuoteSize && this.data.itemQuoteSize.value) {
      this.wrapperEl.nativeElement.style.setProperty(
        "--testimonials-panel-quote-size",
        this.data.itemQuoteSize.value + "px"
      );
    }
    if (this.data.itemQuoteFontWeight && this.data.itemQuoteFontWeight.value) {
      this.wrapperEl.nativeElement.style.setProperty(
        "--testimonials-panel-quote-font-weight",
        this.data.itemQuoteFontWeight.value + "px"
      );
    }
    if (this.data.itemQuoteSize && this.data.itemQuoteSize.value) {
      this.wrapperEl.nativeElement.style.setProperty(
        "--testimonials-panel-subtitle-size",
        this.data.itemQuoteSize.value + "px"
      );
    }
    if (this.data.itemQuoteFontWeight && this.data.itemQuoteFontWeight.value) {
      this.wrapperEl.nativeElement.style.setProperty(
        "--testimonials-panel-subtitle-font-weight",
        this.data.itemQuoteFontWeight.value + "px"
      );
    }

    if (!this.splideInstance) {
      this.mountSlider();
    }
  }

  ngOnDestroy() {
    if (this.splideInstance) {
      this.splideInstance.destroy();
    }
  }

  mountSlider() {
    const options = {
      type: "loop",
      speed: 1000,
      padding: this.isBigPictures ? "15%" : "20%",
      autoplay: true,
      interval: 10000,
      updateOnMove: true,
      easing: "ease-in-out",
      breakpoints: {
        1240: {
          padding: this.isBigPictures ? "15%" : 0,
        },
        1024: {
          gap: "16px",
          padding: 0,
          perPage: 1,
        },
      },
    };

    this.splideInstance = new Splide(this.splideRoot.nativeElement, options);
    this.splideInstance.mount();
  }

  onReadMoreActivated() {
    this.isReadMoreActive = !this.isReadMoreActive;
  }

  mapData() {
    this.items = this.data.items.value;
    this.items.forEach((item: ITestimonialsItemData) => {
      if (item.media && item.media.value) {
        if (item.media.value.video) {
          const desktop = item.media.value.video.desktop;
          const mobile = item.media.value.video.mobile;

          item.media.value.video = {
            desktop: desktop || mobile,
            mobile: mobile || desktop,
          };
        }
        if (item.media.value.image) {
          const desktop = item.media.value.image;
          const mobile = item.mobileMedia && item.mobileMedia.value;
          let mSrc: string;
          let mAlt: string;

          if (mobile) {
            mSrc = mobile.src || desktop.src;
            mAlt = mobile.alt || desktop.alt;
          } else {
            mAlt = desktop.alt;
            mSrc = desktop.src;
          }

          item.imageData = {
            desktopAlt: desktop.alt,
            desktopSrc: desktop.src,
            mobileSrc: mSrc,
            mobileAlt: mAlt,
          };
        }
      }

      item.videoData = this.isMobile ? this.mobile(item) : this.desktop(item);
      item.isShow = false;
      item.bigPictures = this.data.bigPictures.value;
    });

    this.data.waves.value.forEach((item) => {
      switch (item) {
        case "Top Wave":
          this.topWave = true;
          break;
        case "Bottom Wave":
          this.bottomWave = true;
          break;
      }
    });
  }

  mobile(item) {
    return this.prepareVm(get(item, "media.value.video.mobile"));
  }

  desktop(item) {
    return this.prepareVm(get(item, "media.value.video.desktop"));
  }

  private prepareVm(data: IVideoPickerVideoData): IVideoViewModel {
    if (!data) return null;

    return {
      thumbnail: this.thumbnailBuilder.create(data.thumbnail),
      params: `?controls=1&muted=0&autoplay=1&showinfo=0`,
      video: {
        url: data.url,
        type: data.type,
      },
    };
  }

  openVideoModal(item: ITestimonialsItemData) {
    item.isShow = true;
    this.splideInstance.Components.Autoplay.pause();
  }

  closeVideoModal(item: ITestimonialsItemData) {
    item.isShow = false;
    this.splideInstance.Components.Autoplay.play();
  }
}
